/** Browser authenticity states — never map failures to VERIFIED. */
export type AuthState =
  | "INITIALIZING"
  | "VERIFIED"
  | "SUSPICIOUS"
  | "FAKE"
  | "OFFLINE"
  | "EXPIRED"
  | "BLOCKED";

/** Internal runtime trust states for live proof bootstrap (pre Hard Block). */
export type RuntimeTrustState =
  | "INITIALIZING"
  | "BOOTSTRAPPING"
  | "VERIFIED"
  | "STALE"
  | "FAILED";

/** Explicit submit-gate states for Hard Block / pre-submit validation. */
export type SubmitGateState =
  | "IDLE"
  | "VALIDATING"
  | "PERMITTED"
  | "WARNING"
  | "BLOCKED"
  | "ERROR";

/**
 * Public Visual Trust Signal states — derived from RuntimeTrustState + SubmitGateState.
 * Never set directly by customer config or public setters.
 */
export type VisualTrustState =
  | "INITIALIZING"
  | "VERIFYING"
  | "VERIFIED"
  | "WARNING"
  | "BLOCKED"
  | "STALE";

export function generateBrowserNonce(): string {
  const bytes = new Uint8Array(24);
  const g = globalThis as { crypto?: Crypto; Buffer?: { from(data: Uint8Array): { toString(enc: string): string } } };
  if (g.crypto && typeof g.crypto.getRandomValues === "function") {
    g.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  if (typeof btoa === "function") {
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }
  if (g.Buffer) {
    return g.Buffer.from(bytes).toString("base64url");
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export type DemoScenario =
  | "VALID"
  | "WRONG_DOMAIN_SIMULATION"
  | "WRONG_PATH_SIMULATION"
  | "WRONG_FORM_SIMULATION"
  | "EXPIRED_ASSERTION_SIMULATION"
  | "REUSED_NONCE_SIMULATION"
  | "CONTROL_TOWER_OFFLINE_SIMULATION";

export type TimelineEventType =
  | "BOOTSTRAP_REQUESTED"
  | "BOOTSTRAP_RECEIVED"
  | "VERIFY_REQUESTED"
  | "VERDICT_OK"
  | "VERDICT_SUSPICIOUS"
  | "VERDICT_FAKE"
  | "TOKEN_EXPIRED"
  | "SUBMIT_ALLOWED"
  | "SUBMIT_BLOCKED"
  | "PROTECTED_SUBMIT_ACCEPTED"
  | "PROTECTED_SUBMIT_REJECTED"
  | "STATE_CHANGED"
  | "CONTEXT_INVALIDATED"
  | "PRE_SUBMIT_REQUESTED"
  | "PRE_SUBMIT_PERMITTED"
  | "PRE_SUBMIT_BLOCKED"
  | "WARN_SUBMISSION_ALLOWED"
  | "WARN_TRANSPORT_UNAVAILABLE"
  | "WARN_AUTHORIZATION_REFUSED"
  | "SUBMIT_AUTHORIZATION_REQUESTED"
  | "SUBMIT_AUTHORIZATION_RESOLVED";

/** Outcome kind of `ColorShieldClient.authorizeSubmit()`. */
export type SubmitAuthorizationDecision =
  | "PERMIT"
  | "WARN_ALLOW"
  | "WARN_REFUSED"
  | "WARN_TRANSPORT_UNAVAILABLE"
  | "BLOCK";

/**
 * Result of `authorizeSubmit()` — authorization metadata only, never business data.
 *
 * `fields` holds exactly the ColorShield fields the customer's own submit handler
 * should append to its existing request (e.g. FormData), as strings:
 *   PERMIT                     → { submission_permit }
 *   WARN_ALLOW                 → { warn_allow, policy_mode: "WARN", warning_reason }
 *   WARN_REFUSED               → { warn_authorization_refused: "true", policy_mode: "WARN", warning_reason }
 *                                (Control Tower explicitly refused; no permit to consume)
 *   WARN_TRANSPORT_UNAVAILABLE → { warn_transport_unavailable: "true", policy_mode: "WARN" }
 *                                (timeout / network / connection failure / 5xx only)
 *   BLOCK                      → {} (allowed=false; never under WARN policy)
 *
 * Permits are one-time: request a new authorization for every submit attempt.
 */
export interface SubmitAuthorization {
  allowed: boolean;
  decision: SubmitAuthorizationDecision;
  reason_code: string;
  fields: Readonly<Record<string, string>>;
}

export interface ColorShieldConfig {
  /**
   * Optional for browser integrations that resolve tenancy via a server-side
   * bootstrap proxy. Demo environments may still pass an explicit tenantId.
   * Never treat this as a secret.
   */
  tenantId?: string;
  /** Opaque public flow key (cs_flow_...) — identifies configuration, not authenticity. */
  flowKey?: string;
  /** Current SDK config version from Control Tower provisioning. */
  configVersion?: number;
  /**
   * DOM-side form identifier (trust-signal mount scoping via data-form).
   * Also the ColorShield form_context_id when `formContextId` is omitted.
   */
  formId: string;
  /**
   * ColorShield form_context_id used for runtime bootstrap, pre-submit,
   * expected_form_id and legacy verify binding. Defaults to `formId`.
   * Set when the HTML form id differs from the provisioned context
   * (e.g. formId "sourcing-form", formContextId "kolosales-contact-v1").
   */
  formContextId?: string;
  bootstrapUrl: string;
  verifyUrl: string;
  /**
   * Control Tower (or same-origin proxy) pre-submit URL.
   * When set with hardBlock, submit requires an explicit PERMIT.
   */
  preSubmitUrl?: string;
  /**
   * Customer/demo backend protected submit URL.
   * Default: `/demo/protected-submit`
   */
  protectedSubmitUrl?: string;
  /**
   * Policy mode from protected flow (server-provisioned).
   * BLOCK is authoritative Hard Block — client hardBlock=false cannot downgrade.
   * WARN is not silently promoted to allow-submit in this MVP.
   */
  policyMode?: "BLOCK" | "WARN" | string;
  /**
   * Optional Hard Block opt-in for non-BLOCK policies.
   * Ignored as a downgrade switch when policyMode is BLOCK.
   */
  hardBlock?: boolean;
  /**
   * Explicit legacy authenticity-token submit path (demo / older integrations).
   * Required for legacy routing when modern flowKey/policyMode are absent.
   * Never used as a silent fallback for BLOCK or modern provisioned flows.
   */
  legacyAuthenticitySubmit?: boolean;
  /**
   * Pre-submit HTTP timeout (ms). Prefer protocol PRE_SUBMIT_TIMEOUT_MS.
   */
  preSubmitTimeoutMs?: number;
  /**
   * When false, start() installs NO submit listener on the protected form.
   * The customer's own submit handler stays authoritative and calls
   * `authorizeSubmit()` itself, appending the returned fields to its request.
   * Default true (SDK intercepts submit and posts to protectedSubmitUrl).
   */
  interceptSubmit?: boolean;
  /**
   * Same-origin runtime bootstrap URL (customer backend → Control Tower).
   * When set, start() bootstraps the live runtime proof (requires flowKey) and
   * the Visual Trust Signal / getState() follow it; the legacy authenticity
   * bootstrap/verify loop is not run. `authorizeSubmit()` also uses it to
   * refresh a missing or stale proof. If omitted, start() keeps legacy behaviour
   * and the last `bootstrapRuntimeTrust()` input is reused.
   */
  runtimeBootstrapUrl?: string;
  protectedFormSelector: string;
  indicatorSelector: string;
  /**
   * Optional mount for the Visual Trust Signal.
   * Prefer a host with data-colorshield-trust, or omit to auto-place near the form.
   * Cannot force VERIFIED / WARNING / BLOCKED — presentation only.
   */
  trustSignalSelector?: string;
  /**
   * When false, disables Visual Trust Signal mounting (default true when DOM present).
   * Cannot override authoritative trust state.
   */
  trustSignalEnabled?: boolean;
  /** Optional public flow type label for details disclosure (e.g. login, checkout). */
  flowType?: string;
  /**
   * When true, start() binds DOM + Visual Trust Signal but skips the legacy
   * authenticity bootstrap/verify loop. Used by the commercial runtime-trust path
   * (Hard Block + live proof). Default false.
   */
  skipAuthenticityVerify?: boolean;
  refreshBeforeExpirySeconds?: number;
  sessionId?: string;
  /**
   * DEMO/TEST ONLY. Must be explicitly enabled.
   * Never enable in production integrations.
   */
  allowDemoScenarios?: boolean;
  demoScenario?: DemoScenario;
  onStateChange?: (state: AuthState, detail: StateDetail) => void;
  /** Fired when the Visual Trust Signal derived state changes. */
  onVisualTrustChange?: (
    state: VisualTrustState,
    detail: {
      state: VisualTrustState;
      label: string;
      message: string;
      hostname?: string;
      flowType?: string;
      policyMode?: string;
      lastVerifiedAt?: string;
    }
  ) => void;
  onTimeline?: (event: TimelineEvent) => void;
  /** Injected for tests */
  fetchImpl?: typeof fetch;
  now?: () => number;
  location?: { hostname: string; pathname: string; origin: string };
  /** Injected document for tests (jsdom / happy-dom). */
  document?: Document;
}

export interface StateDetail {
  state: AuthState;
  verdict?: string;
  reason_code?: string;
  message: string;
  expires_at?: string;
  token_present: boolean;
}

export interface TimelineEvent {
  ts: string;
  type: TimelineEventType;
  detail?: Record<string, unknown>;
}

export interface BootstrapPayload {
  bootstrap_assertion: string;
  expires_at: string;
  nonce: string;
  session_id: string;
  tenant_id?: string;
}

export interface VerifyResponse {
  verdict: "OK" | "SUSPICIOUS" | "FAKE";
  reason_code: string;
  authenticity_token?: string;
  issued_at?: string;
  expires_at?: string;
}

export const STATE_MESSAGES: Record<AuthState, string> = {
  INITIALIZING: "Security verification in progress",
  VERIFIED: "Protected by ColorShield ID",
  SUSPICIOUS: "Security verification failed",
  FAKE: "Form authenticity could not be verified",
  OFFLINE: "Security verification unavailable",
  EXPIRED: "Security verification expired",
  BLOCKED: "Submission blocked for your protection",
};
